// pages/admin/BlogList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  MdAdd, MdSearch, MdEdit, MdDelete, MdArticle, MdOpenInNew,
} from 'react-icons/md';

export default function BlogList() {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const limit = 20;

  // ── Fetch list ──
  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();

      const res = await axios.get(`${base_url}/api/v1/blogs/admin/list`, {
        params,
      });

      if (res.data.success) {
        setBlogs(res.data.data?.data || []);
        setPagination(
          res.data.data?.pagination || { total: 0, totalPages: 0 }
        );
      } else {
        setBlogs([]);
      }
    } catch (err) {
      console.error('Fetch blogs error:', err);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [base_url, page, search]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      if (page !== 1) setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Delete ──
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete blog "${title}"? This cannot be undone.`)) return;

    setActionLoading((p) => ({ ...p, [id]: true }));
    try {
      await axios.delete(`${base_url}/api/v1/blogs/${id}`);
      setBlogs((prev) => prev.filter((b) => b.id !== id));
      setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
    } catch (err) {
      console.error('Delete error:', err);
      alert(err?.response?.data?.message || 'Failed to delete');
    } finally {
      setActionLoading((p) => ({ ...p, [id]: false }));
    }
  };

  // ── Toggle publish ──
  const handleTogglePublish = async (id, current) => {
    setBlogs((prev) =>
      prev.map((b) => (b.id === id ? { ...b, is_published: !current } : b))
    );
    try {
      await axios.patch(`${base_url}/api/v1/blogs/${id}/publish`, {
        is_published: !current,
      });
    } catch (err) {
      setBlogs((prev) =>
        prev.map((b) => (b.id === id ? { ...b, is_published: current } : b))
      );
      alert(err?.response?.data?.message || 'Failed to update');
    }
  };

  // ── Toggle featured ──
  const handleToggleFeatured = async (id, current) => {
    setBlogs((prev) =>
      prev.map((b) => (b.id === id ? { ...b, is_featured: !current } : b))
    );
    try {
      await axios.patch(`${base_url}/api/v1/blogs/${id}/featured`, {
        is_featured: !current,
      });
    } catch (err) {
      setBlogs((prev) =>
        prev.map((b) => (b.id === id ? { ...b, is_featured: current } : b))
      );
      alert(err?.response?.data?.message || 'Failed to update');
    }
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="fw-bold mb-0">
              <MdArticle className="me-2 text-primary" />
              Blog Management
            </h4>
            <Link to="/dashboard/blogs/create" className="btn btn-primary">
              <MdAdd /> Add Blog
            </Link>
          </div>

          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <MdSearch />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by title or category..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>Blog</th>
                    <th>Category</th>
                    <th>Views</th>
                    <th>Published</th>
                    <th>Featured</th>
                    <th>Date</th>
                    <th style={{ width: 220 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(8)].map((__, j) => (
                          <td key={j}>
                            <Skeleton />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : blogs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        <MdArticle size={40} />
                        <div className="mt-2">No blogs yet</div>
                        <Link
                          to="/dashboard/blogs/create"
                          className="btn btn-sm btn-primary mt-2"
                        >
                          Create your first blog
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    blogs.map((b, idx) => (
                      <tr key={b.id}>
                        <td>{(page - 1) * limit + idx + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {b.image && (
                              <img
                                src={b.image}
                                alt=""
                                width="50"
                                height="50"
                                style={{
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                }}
                              />
                            )}
                            <div>
                              <strong>{b.title}</strong>
                              <div className="small text-muted">{b.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {b.category ? (
                            <span className="badge bg-info text-light">
                              {b.category}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{b.views ?? 0}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!!b.is_published}
                              onChange={() =>
                                handleTogglePublish(b.id, b.is_published)
                              }
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!!b.is_featured}
                              onChange={() =>
                                handleToggleFeatured(b.id, b.is_featured)
                              }
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        </td>
                        <td>
                          {b.published_at
                            ? new Date(b.published_at).toLocaleDateString(
                                'en-IN'
                              )
                            : '-'}
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Link
                              to={`/dashboard/blogs/edit/${b.id}`}
                              className="btn btn-sm btn-primary"
                            >
                              <MdEdit /> Edit
                            </Link>
                            <a
                              href={`/blogs/${b.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                            >
                              <MdOpenInNew />
                            </a>
                            <button
                              className="btn btn-sm btn-danger"
                              disabled={actionLoading[b.id]}
                              onClick={() => handleDelete(b.id, b.title)}
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Showing {Math.min((page - 1) * limit + 1, pagination.total)} -{' '}
                  {Math.min(page * limit, pagination.total)} of {pagination.total}
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page <strong>{page}</strong> of{' '}
                    <strong>{pagination.totalPages}</strong>
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}